import { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { io } from "socket.io-client";
import { jwtDecode } from "jwt-decode";
import { FiSend } from "react-icons/fi";

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || "http://127.0.0.1:9001";
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:9001";
const ADMIN_ROLES = ["ADMIN", "AADMIN", "SUPERADMIN"];

const formatTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  let h = date.getHours();
  const m = date.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m} ${ampm}`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) return formatTime(timestamp);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const authHeaders = (token) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

const Avatar = ({ name, size = 36 }) => {
  const initials = (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const colors = [
    "#0d6efd",
    "#6f42c1",
    "#d63384",
    "#fd7e14",
    "#20c997",
    "#0dcaf0",
  ];
  const color = colors[(name?.charCodeAt(0) || 0) % colors.length];

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.38,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
};

const MessageTicks = ({ status }) => {
  if (status === "sending") {
    return (
      <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
        <path
          d="M1 5l3.5 3.5L12.5 1"
          stroke="#94a3b8"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (status === "sent") {
    return (
      <svg width="18" height="10" viewBox="0 0 18 10" fill="none">
        <path
          d="M1 5l3.5 3.5L12.5 1"
          stroke="#94a3b8"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5 5l3.5 3.5L16.5 1"
          stroke="#94a3b8"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (status === "read") {
    return (
      <svg width="18" height="10" viewBox="0 0 18 10" fill="none">
        <path
          d="M1 5l3.5 3.5L12.5 1"
          stroke="#53bdeb"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M5 5l3.5 3.5L16.5 1"
          stroke="#53bdeb"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return null;
};

const chatUi = {
  panel: {
    position: "fixed",
    bottom: "70px",
    right: "24px",
    width: "380px",
    height: "560px",
    background: "#ffffff",
    borderRadius: "18px",
    boxShadow: "0 18px 45px rgba(15, 23, 42, 0.12)",
    zIndex: 99991,
    overflow: "hidden",
    animation: "chatPanelIn 0.25s ease",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #e5e7eb",
  },

  header: {
    padding: "14px 16px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#ffffff",
  },

  backBtn: {
    width: "30px",
    height: "30px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    color: "#475569",
    background: "#f1f5f9",
    fontSize: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  listHeader: {
    padding: "16px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
  },

  listItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "14px 16px",
    cursor: "pointer",
    borderBottom: "1px solid #f1f5f9",
    background: "#ffffff",
    transition: "all 0.15s ease",
  },

  messagesWrap: {
    flex: 1,
    overflowY: "auto",
    padding: "14px 12px",
    background: "#f5f7fa",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  senderBubble: {
    maxWidth: "78%",
    padding: "10px 13px",
    borderRadius: "16px 16px 4px 16px",
    background: "#ffffff",
    color: "#111827",
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: 1.5,
  },

  receiverBubble: {
    maxWidth: "78%",
    padding: "10px 13px",
    borderRadius: "16px 16px 16px 4px",
    background: "#D9FDD3",
    color: "#111827",
    border: "1px solid #cceec6",
    boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
    fontSize: "13px",
    fontWeight: 500,
    lineHeight: 1.5,
  },

  inputWrap: {
    padding: "10px 12px",
    borderTop: "1px solid #e5e7eb",
    background: "#ffffff",
  },

  inputInner: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "999px",
    padding: "6px 6px 6px 14px",
  },

  input: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "13px",
    fontWeight: 500,
    background: "transparent",
    color: "#111827",
  },

  sendBtn: {
    background: "#25D366",
    color: "#ffffff",
    border: "none",
    borderRadius: "50%",
    width: "36px",
    height: "36px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 4px 12px rgba(37,211,102,0.25)",
  },
};

const ChatWindow = ({
  vendor,
  currentUser,
  token,
  socket,
  onBack,
  onNewMessage,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [typingUser, setTypingUser] = useState("");
  const [tickMap, setTickMap] = useState({});
  const messagesEndRef = useRef(null);
  const groupId = vendor.groupId;

  const scrollToBottom = () =>
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  const fetchMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}socket/api/getMessagesBygroupId`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ groupId }),
      });
      const data = await res.json();
      const msgs = Array.isArray(data) ? data : data?.messages || [];
      setMessages(msgs);

      const newTickMap = {};
      msgs.forEach((msg) => {
        if (msg.senderId === currentUser.userId) {
          const isRead =
            Array.isArray(msg.readBy) &&
            msg.readBy.some((id) => id !== currentUser.userId);
          newTickMap[msg._id || msg.tempId] = isRead ? "read" : "sent";
        }
      });
      setTickMap((prev) => ({ ...prev, ...newTickMap }));
      setTimeout(scrollToBottom, 100);

      fetch(`${API_BASE}socket/api/markMessagesRead`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ groupId, userId: currentUser.userId }),
      }).catch(console.error);

      socket?.emit("messagesRead", { groupId, readerId: currentUser.userId });
    } catch (err) {
      console.error("fetchMessages err:", err);
    }
  }, [groupId, token, currentUser.userId, socket]);

  useEffect(() => {
    if (!groupId || !socket) return;
    socket.emit("joinRoom", { groupId });
    fetchMessages();

    const handleNewMessage = (msg) => {
      if (msg.groupId === groupId) {
        fetchMessages();
        onNewMessage && onNewMessage(groupId, true);
      }
    };

    const handleTyping = ({ userName, groupId: chatId }) => {
      if (chatId === groupId && userName !== currentUser.name) {
        setTypingUser(userName);
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 3000);
      }
    };

    const handleMessagesRead = ({ groupId: readGroupId }) => {
      if (readGroupId === groupId) {
        setTickMap((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((key) => {
            if (updated[key] === "sent") updated[key] = "read";
          });
          return updated;
        });
      }
    };

    socket.on("newMessage", handleNewMessage);
    socket.on("typing", handleTyping);
    socket.on("messagesRead", handleMessagesRead);

    return () => {
      socket.emit("leaveRoom", groupId);
      socket.off("newMessage", handleNewMessage);
      socket.off("typing", handleTyping);
      socket.off("messagesRead", handleMessagesRead);
    };
  }, [groupId, socket, fetchMessages, currentUser.name, onNewMessage]);

  const handleSend = () => {
    if (!input.trim() || !socket) return;

    const tempId = `temp_${Date.now()}`;
    const newMsg = {
      tempId,
      groupId,
      chatId: groupId,
      senderId: currentUser.userId,
      senderType: currentUser.type,
      senderName: currentUser.name,
      message: input,
      timestamp: new Date().toISOString(),
    };

    socket.emit("sendMessage", newMsg);
    setMessages((prev) => [...prev, newMsg]);
    setTickMap((prev) => ({ ...prev, [tempId]: "sending" }));

    setTimeout(() => {
      setTickMap((prev) => ({ ...prev, [tempId]: "sent" }));
    }, 600);

    setInput("");
    setTimeout(scrollToBottom, 50);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={chatUi.header}>
        <button onClick={onBack} style={chatUi.backBtn}>
          ‹
        </button>
        <Avatar name={vendor.vendorName} size={36} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: "14px", color: "#0f172a" }}>
            {vendor.vendorName}
          </div>
          <div style={{ fontSize: "11px", color: "#64748b" }}>
            {vendor.vendorId}
          </div>
        </div>
      </div>

      <div style={chatUi.messagesWrap}>
        {messages.map((msg, i) => {
          const isSender = msg.senderId === currentUser.userId;
          const msgKey = msg._id || msg.tempId || i;
          const tickStatus = isSender ? tickMap[msgKey] || "sent" : null;

          return (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: isSender ? "flex-end" : "flex-start",
              }}
            >
              <div
                style={isSender ? chatUi.senderBubble : chatUi.receiverBubble}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    marginTop: "5px",
                    gap: "6px",
                    fontSize: "10px",
                    fontWeight: 500,
                    color: "#64748b",
                  }}
                >
                  {isSender ? "You" : msg.senderName || vendor.vendorName}
                </div>

                <div
                  dangerouslySetInnerHTML={{
                    __html: (msg.message || "").replace(
                      /@(\w+)/g,
                      '<span style="color:#fde68a;font-weight:700;">@$1</span>',
                    ),
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "4px",
                    marginTop: "6px",
                    fontSize: "11px",
                  }}
                >
                  <span style={{ color: "#94a3b8" }}>
                    {formatTime(msg.timestamp)}
                  </span>
                  {isSender && (
                    <span style={{ marginLeft: "auto", display: "inline-flex" }}>
                      <MessageTicks status={tickStatus} />
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {isTyping && (
          <div
            style={{
              fontStyle: "italic",
              fontSize: "12px",
              color: "#64748b",
              paddingLeft: "4px",
            }}
          >
            {typingUser} is typing...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div style={chatUi.inputWrap}>
        <div style={chatUi.inputInner}>
          <input
            type="text"
            placeholder={`Message ${vendor.vendorName}...`}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              socket?.emit("typing", { userName: currentUser.name, groupId });
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSend()}
            style={chatUi.input}
          />
          <button onClick={handleSend} style={chatUi.sendBtn}>
            <FiSend size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};

const VendorList = ({ chatList, loading, onSelect, unreadMap }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={chatUi.listHeader}>
        <div style={{ fontWeight: 700, fontSize: "16px", color: "#0f172a" }}>
          💬 Chats
        </div>
        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
          {chatList.length} vendor conversation
          {chatList.length !== 1 ? "s" : ""}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", background: "#fff" }}>
        {loading ? (
          <div
            style={{ textAlign: "center", padding: "30px", color: "#64748b" }}
          >
            <div
              className="spinner-border spinner-border-sm me-2"
              role="status"
            />
            Loading chats...
          </div>
        ) : chatList.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#94a3b8",
            }}
          >
            <i
              className="bi bi-chat-slash"
              style={{
                fontSize: "32px",
                display: "block",
                marginBottom: "8px",
              }}
            />
            No chats yet
          </div>
        ) : (
          chatList.map((vendor) => {
            const unread = unreadMap[vendor.groupId] || vendor.unreadCount || 0;

            return (
              <div
                key={vendor.groupId}
                onClick={() => onSelect(vendor)}
                style={chatUi.listItem}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f8fafc")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#fff")
                }
              >
                <div style={{ position: "relative" }}>
                  <Avatar name={vendor.vendorName} size={42} />
                  {unread > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        top: "-2px",
                        right: "-2px",
                        background: "#16a34a",
                        color: "#fff",
                        borderRadius: "999px",
                        minWidth: "18px",
                        height: "18px",
                        padding: "0 5px",
                        fontSize: "10px",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid #fff",
                      }}
                    >
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "4px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: unread > 0 ? 700 : 600,
                        fontSize: "13px",
                        color: "#0f172a",
                      }}
                    >
                      {vendor.vendorName}
                    </span>
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#94a3b8",
                        flexShrink: 0,
                      }}
                    >
                      {formatDate(vendor.lastTimestamp)}
                    </span>
                  </div>

                  <div
                    style={{
                      fontSize: "12px",
                      color: unread > 0 ? "#334155" : "#64748b",
                      fontWeight: unread > 0 ? 600 : 400,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {vendor.lastSenderId !== vendor.vendorId
                      ? `You: ${vendor.lastMessage}`
                      : vendor.lastMessage}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const AdminChatIcon = () => {
  const [open, setOpen] = useState(false);
  const [chatList, setChatList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [totalUnread, setTotalUnread] = useState(0);
  const [unreadMap, setUnreadMap] = useState({});
  const socketRef = useRef(null);

  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : {};
  const isAdmin = decoded?.roles?.some((r) => ADMIN_ROLES.includes(r.roleId));

  const currentUser = {
    userId: decoded?.userId,
    name: decoded?.userName,
    type: decoded?.roles?.[0]?.roleId,
  };

  useEffect(() => {
    if (!isAdmin) return;

    socketRef.current = io(SOCKET_URL, { transports: ["websocket"] });
    socketRef.current.on("connect", () =>
      console.log("✅ Admin chat socket connected"),
    );
    socketRef.current.on("newMessage", (msg) => {
      if (msg.senderId !== currentUser.userId) {
        setUnreadMap((prev) => ({
          ...prev,
          [msg.groupId]: (prev[msg.groupId] || 0) + 1,
        }));
        setTotalUnread((prev) => prev + 1);
      }
    });

    return () => socketRef.current?.disconnect();
     // eslint-disable-next-line
  }, [isAdmin]);

  const fetchChatList = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}socket/api/getAdminChatList`, {
        method: "POST",
        headers: authHeaders(token),
        body: JSON.stringify({ adminId: currentUser.userId }),
      });
      const data = await res.json();
      const list = data?.data || [];
      setChatList(list);

      const map = {};
      let total = 0;
      list.forEach((v) => {
        map[v.groupId] = v.unreadCount || 0;
        total += v.unreadCount || 0;
      });

      setUnreadMap((prev) => {
        const merged = { ...prev };
        Object.keys(map).forEach((k) => {
          merged[k] = Math.max(prev[k] || 0, map[k]);
        });
        return merged;
      });

      setTotalUnread((prev) => Math.max(prev, total));
    } catch (err) {
      console.error("fetchChatList err:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    fetchChatList();
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedVendor(null);
  };

  const handleSelectVendor = (vendor) => {
    setSelectedVendor(vendor);
    const wasUnread = unreadMap[vendor.groupId] || 0;
    setUnreadMap((prev) => ({ ...prev, [vendor.groupId]: 0 }));
    setTotalUnread((prev) => Math.max(0, prev - wasUnread));
  };

  const handleBack = () => {
    setSelectedVendor(null);
    fetchChatList();
  };

  const handleNewMessageInWindow = (groupId, isRead) => {
    if (isRead) setUnreadMap((prev) => ({ ...prev, [groupId]: 0 }));
  };

  if (!isAdmin) return null;

  return (
    <>
      {open &&
        ReactDOM.createPortal(
          <>
            <style>{`
              @keyframes chatPanelIn {
                from { opacity: 0; transform: translateY(18px) scale(0.98); }
                to { opacity: 1; transform: translateY(0) scale(1); }
              }
            `}</style>

            <div
              onClick={handleClose}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 99990,
                background: "transparent",
              }}
            />

            <div style={chatUi.panel}>
              {selectedVendor ? (
                <ChatWindow
                  vendor={selectedVendor}
                  currentUser={currentUser}
                  token={token}
                  socket={socketRef.current}
                  onBack={handleBack}
                  onNewMessage={handleNewMessageInWindow}
                />
              ) : (
                <VendorList
                  chatList={chatList}
                  loading={loading}
                  onSelect={handleSelectVendor}
                  unreadMap={unreadMap}
                />
              )}
            </div>
          </>,
          document.body,
        )}

      <li className="nav-item dropdown pe-4" title="Chats">
        <button
          className="nav-link nav-icon border-0 bg-transparent"
          type="button"
          onClick={open ? handleClose : handleOpen}
          aria-label="Chats"
        >
          <i className="bi bi-chat-left-text"></i>
          {totalUnread > 0 && (
            <span className="badge bg-success badge-number">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          )}
        </button>
      </li>
    </>
  );
};

export default AdminChatIcon;